/**
 * Registo do service worker — scope = diretório frontend (pai de /js).
 * Atualizações silenciosas: ativa SW pendente e recarrega sem banner quando seguro.
 */
import { PWA_CACHE_VERSION } from './pwa-version.js';

const SW_URL = new URL('../sw.js', import.meta.url);
const SCOPE_URL = new URL('../', import.meta.url);
const PWA_VERSION_MARKER_KEY = 'ph_pwa_cache_version';

/** @type {ServiceWorkerRegistration | null} */
let activeRegistration = null;

/** @type {boolean} */
let silentReloadScheduled = false;

export function getServiceWorkerRegistration() {
  return activeRegistration;
}

export { PWA_CACHE_VERSION };

function readPersistedPwaVersion() {
  try {
    return String(localStorage.getItem(PWA_VERSION_MARKER_KEY) || '').trim();
  } catch {
    return '';
  }
}

function persistPwaVersion(version) {
  try {
    localStorage.setItem(PWA_VERSION_MARKER_KEY, String(version || '').trim());
  } catch {
    /* ignore */
  }
}

/**
 * Garante que clientes antigos forcem update do SW quando a versão do app muda.
 * Retorna true quando detecta migração de versão.
 * @param {ServiceWorkerRegistration} reg
 */
async function reconcilePwaCacheVersion(reg) {
  const prev = readPersistedPwaVersion();
  const current = String(PWA_CACHE_VERSION || '').trim();
  if (!current) return false;
  if (prev === current) return false;
  persistPwaVersion(current);
  try {
    await reg.update();
  } catch {
    /* offline */
  }
  if (reg.waiting && navigator.serviceWorker.controller) {
    activateWaitingWorker(reg);
  }
  return true;
}

/** Remove banner/versão legados da UI (deploys anteriores). */
export function removeLegacyPwaUi() {
  if (typeof document === 'undefined') return;
  document.querySelectorAll('[data-wm-app-version]').forEach((el) => el.remove());
  document.getElementById('wmPwaUpdateBanner')?.remove();
  document.body.classList.remove('wm-has-pwa-update-banner');
}

/** Evita reload a meio de preenchimento de formulário. */
export function canSilentReloadNow() {
  if (typeof document === 'undefined') return true;
  const el = document.activeElement;
  if (!el || typeof el !== 'object') return true;
  const tag = /** @type {{ tagName?: string; isContentEditable?: boolean }} */ (el).tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return false;
  if (/** @type {{ isContentEditable?: boolean }} */ (el).isContentEditable) return false;
  return true;
}

/** @param {ServiceWorkerRegistration} reg */
function activateWaitingWorker(reg) {
  const waiting = reg.waiting;
  if (!waiting) return;
  waiting.postMessage({ type: 'SKIP_WAITING' });
}

/** Recarrega uma vez quando o novo SW assume — sem UI de update. */
export function scheduleSilentReload() {
  if (typeof window === 'undefined' || silentReloadScheduled) return;
  silentReloadScheduled = true;

  const reload = () => {
    if (!canSilentReloadNow()) {
      silentReloadScheduled = false;
      document.addEventListener('focusout', () => scheduleSilentReload(), { once: true });
      return;
    }
    window.location.reload();
  };

  if (document.readyState === 'complete') {
    window.requestAnimationFrame(() => window.setTimeout(reload, 80));
    return;
  }

  window.addEventListener(
    'load',
    () => {
      window.requestAnimationFrame(() => window.setTimeout(reload, 80));
    },
    { once: true },
  );
}

/** @param {ServiceWorkerRegistration} reg */
function watchForServiceWorkerUpdates(reg) {
  const applyUpdate = () => activateWaitingWorker(reg);

  if (reg.waiting && navigator.serviceWorker.controller) {
    applyUpdate();
  }

  reg.addEventListener('updatefound', () => {
    const sw = reg.installing;
    if (!sw) return;
    sw.addEventListener('statechange', () => {
      if (sw.state === 'installed' && navigator.serviceWorker.controller) {
        applyUpdate();
      }
    });
  });

  const checkForUpdates = () => {
    if (document.visibilityState !== 'visible') return;
    void reg.update().catch(() => {
      /* offline */
    });
  };

  document.addEventListener('visibilitychange', checkForUpdates);
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) checkForUpdates();
  });
}

/**
 * @param {{ reloadOnUpdate?: boolean, activateWaiting?: boolean }} [opts]
 * - `reloadOnUpdate`: recarrega silenciosamente ao trocar SW (default true).
 * - `activateWaiting`: aplica SW pendente de imediato (default true).
 */
export async function registerPetroHouseServiceWorker(opts = {}) {
  const reloadOnUpdate = opts.reloadOnUpdate !== false;
  const activateWaiting = opts.activateWaiting !== false;

  removeLegacyPwaUi();
  if (!('serviceWorker' in navigator)) return false;
  try {
    let scope = SCOPE_URL.href;
    if (!scope.endsWith('/')) scope += '/';
    const reg = await navigator.serviceWorker.register(SW_URL.href, { scope });
    activeRegistration = reg;
    const versionMigrated = await reconcilePwaCacheVersion(reg);

    if (activateWaiting) {
      watchForServiceWorkerUpdates(reg);
    }

    if (reloadOnUpdate) {
      const hadController = !!navigator.serviceWorker.controller;
      let reloaded = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (!hadController || reloaded) return;
        reloaded = true;
        scheduleSilentReload();
      });
    }

    void reg.update().catch(() => {
      /* offline no arranque */
    });
    if (versionMigrated && reg.waiting && navigator.serviceWorker.controller) {
      scheduleSilentReload();
    }

    return true;
  } catch (err) {
    console.warn('[SysFactory PWA] Service worker não registado:', err);
    return false;
  }
}
